import { useMemo } from 'react';
import classNames from 'classnames';

import BrandedImagery from '../branded-imagery/branded-imagery.component';
import { Button } from '../button/button.component';
import { ContentBlockProps } from './content-block.config';
import { Icon } from '../icon/icon.component';
import { ImageEmbedded } from '../image/image-embedded';
import { MinHeight } from '../min-height/min-height';
import { Text } from '../text/text.component';
import { Title } from '../title/title.component';

export enum ContentBlockBrandedImageryColorTypes {
  FIRST_LIGHT = 'snowflake-content-block-first-light-indicator',
  PURPLE_MOON = 'snowflake-content-block-purple-moon-indicator',
  STAR_BLUE = 'snowflake-content-block-star-blue-indicator',
  VALENCIA_ORANGE = 'snowflake-content-block-valencia-orange-indicator',
}

const ContentBlock = ({
  additionalClasses,
  appliedCssClassNames,
  brandedImagery,
  cta,
  ctaMinHeight,
  ctaRef,
  icon,
  logo,
  text,
  textMinHeight,
  textRef,
  title,
  titleMinHeight,
  titleRef,
}: ContentBlockProps) => {
  const darkBg = appliedCssClassNames?.includes('snowflake-content-block-dark-bg');
  const firstLightIndicator = appliedCssClassNames?.includes(ContentBlockBrandedImageryColorTypes.FIRST_LIGHT);
  const purpleMoonIndicator = appliedCssClassNames?.includes(ContentBlockBrandedImageryColorTypes.PURPLE_MOON);
  const starBlueIndicator = appliedCssClassNames?.includes(ContentBlockBrandedImageryColorTypes.STAR_BLUE);
  const valenciaOrangeIndicator = appliedCssClassNames?.includes(ContentBlockBrandedImageryColorTypes.VALENCIA_ORANGE);

  const styles = useMemo(
    () => ({
      icon: {
        'snowflake-icon-blue': !darkBg,
        'snowflake-icon-white': darkBg,
      },
      brandedImagery: {
        'white-imagery-background': !darkBg,
        'blue-imagery-background': darkBg,
        'first-light-imagery-indicator': firstLightIndicator,
        'purple-moon-imagery-indicator': purpleMoonIndicator,
        'star-blue-imagery-indicator': starBlueIndicator,
        'valencia-orange-imagery-indicator': valenciaOrangeIndicator,
      },
      title: {
        'snowflake-content-block-title-default': !darkBg,
        'snowflake-content-block-title-dark-bg': darkBg,
      },
      text: {
        'text-color-text-04-ui-bg-03': darkBg,
        'text-size-regular': true,
      },
      cta: {
        'snowflake-button-blue': !darkBg,
        'snowflake-button-white': darkBg,
      },
    }),
    [darkBg, firstLightIndicator, purpleMoonIndicator, starBlueIndicator, valenciaOrangeIndicator]
  );

  return (
    <div data-testid="snowflake-content-block" className={classNames('snowflake-content-block', additionalClasses)}>
      {logo && (
        <div data-testid="snowflake-content-block-logo" className="snowflake-content-block-logo snowflake-logo">
          <ImageEmbedded {...logo} baseCssClass="cmp-image" />
        </div>
      )}
      {icon && (
        <div data-testid="snowflake-content-block-icon" className={classNames(styles.icon)}>
          <Icon {...icon} />
        </div>
      )}
      {brandedImagery && (
        <div data-testid="snowflake-content-block-branded-imagery" className={classNames(styles.brandedImagery)}>
          <BrandedImagery {...brandedImagery} />
        </div>
      )}
      {title ? (
        <MinHeight
          data-testid="snowflake-content-block-title"
          className={classNames(styles.title)}
          minHeight={titleMinHeight}
          childRef={titleRef}
        >
          <Title {...title} />
        </MinHeight>
      ) : null}
      {text && (
        <MinHeight
          data-testid="snowflake-content-block-text"
          className={classNames(styles.text)}
          minHeight={textMinHeight}
          childRef={textRef}
        >
          <Text {...text} />
        </MinHeight>
      )}
      {cta && (
        <MinHeight
          data-testid="snowflake-content-block-cta"
          className={classNames('snowflake-button-primary snowflake-button-regular', styles.cta)}
          minHeight={ctaMinHeight}
          childRef={ctaRef}
        >
          <Button {...cta} />
        </MinHeight>
      )}
    </div>
  );
};

export default ContentBlock;
